import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface RankingChartProps {
  rankings: Array<{
    resumeFilename: string;
    score: number;
    rank: number;
    strengths: string[];
    improvements: string[];
  }>;
  onBarClick?: (ranking: any) => void;
  selectedCandidate?: any;
}

const RankingChart: React.FC<RankingChartProps> = ({ rankings, onBarClick, selectedCandidate }) => {
  const { theme } = useTheme();
  const isDark = theme === 'colorful';

  console.log('RankingChart selectedCandidate:', selectedCandidate?.resumeFilename);

  // Sort rankings by score descending to match reference image
  const sortedRankings = [...rankings].sort((a, b) => b.score - a.score);

  // Prepare data for horizontal bar chart
  const chartData = {
    labels: sortedRankings.map(r => r.resumeFilename.replace(/\.[^/.]+$/, '')), // Remove file extension
    datasets: [
      {
        label: 'Match Score (%)',
        data: sortedRankings.map(r => r.score),
        backgroundColor: sortedRankings.map((r, index) => {
          const isSelected = selectedCandidate && r.resumeFilename === selectedCandidate.resumeFilename;
          if (isSelected) {
            return 'rgba(234, 88, 12, 0.95)';   // Deep orange for selected
          }
          if (index === 0) {
            return 'rgba(251, 146, 60, 0.9)';    // Bright orange for top
          }
          return 'rgba(251, 146, 60, 0.55)';     // Softer orange for others
        }),
        borderColor: sortedRankings.map((r, index) => {
          if (selectedCandidate && r.resumeFilename === selectedCandidate.resumeFilename) {
            return 'rgba(234, 88, 12, 1)';       // Deep orange border for selected
          }
          if (index === 0) {
            return 'rgba(251, 146, 60, 1)';
          }
          return 'rgba(251, 146, 60, 0.75)';
        }),
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 40,
        maxBarThickness: 40,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend to match reference
      },
      title: {
        display: false, // Title is handled by the parent component
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // Clean look like reference
        },
        ticks: {
          color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
          font: {
            size: 11,
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          maxRotation: 45,
          padding: 8,
        },
        border: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
          lineWidth: 1,
        },
        ticks: {
          color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
          font: {
            size: 12,
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          callback: function (value) {
            return value + '%';
          },
        },
        border: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeOutQuart',
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    },
    onClick: (event: any, elements: any) => {
      if (elements.length > 0 && onBarClick) {
        const dataIndex = elements[0].index;
        const ranking = sortedRankings[dataIndex];
        if (ranking) {
          onBarClick(ranking);
        }
      }
    },
  };

  return (
    <div className="ranking-chart-wrapper" style={{ height: '400px', width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default RankingChart;